import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import type { MenuItem } from "@/VisualBuilder/components/LeftHandMenu/types";
import {
  ELEMENT_STATUS,
  ELEMENT_TYPE,
  ENTITY,
  SEARCH_OPTION_SECTION,
  SORT_FIELD,
  SORT_ORIENTATION,
  TAB_STATUSES,
} from "@/constants";
import {
  useContentElementsData,
  useCurrentExperienceData,
  useCurrentExperiencesData,
  useCurrentMediaItemData,
  useCurrentMediaShare,
  useCurrentPropertyData,
  useCurrentShareData,
  useElementsData,
  useExperienceMediaItemsData,
  useHotspotsData,
  useIconsData,
  useSearchOptionsData,
  useUrlParams,
} from "@/hooks";
import { useThunkDispatch } from "@/store/hooks";
import { getExperienceValues } from "@/utils/experienceUtils";
import { getPropertyMenuItems } from "@/utils/propertyUtils";

import { getVisualBuilderData } from "../../store/actions";
import {
  currentElementSelector,
  currentExperienceSelector,
  currentPropertySelector,
  visualBuilderDataSelector,
  visualBuilderValuesSelector,
} from "../../store/selectors";
import { setDashboard } from "../components/MainHeader/MainHeaderDataSlice";

export const useVisualBuilderData = () => {
  const dispatch = useThunkDispatch();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const params = useUrlParams();
  const { propertyId, experienceId = null, tabStatus, userId } = params;

  const { property } = useSelector(currentPropertySelector);
  const { experience } = useSelector(currentExperienceSelector);

  const { element } = useSelector(currentElementSelector);

  const { id: elementId } = element;

  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));

  const { currentMediaItemId } = visualBuilderData;

  const { prevTabStatus } = useSelector(visualBuilderValuesSelector(propertyId));

  useCurrentPropertyData();

  useEffect(() => {
    dispatch(setDashboard({ dashboardStatus: false }));
  }, []);

  let newParams: typeof params & { propertyMenuItems: MenuItem[] };

  const fetchVisualBuilderData = (values = {}) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    newParams = {
      ...params,
      propertyMenuItems: property.propertyMenuItems,
    };

    timer.current = setTimeout(() => {
      dispatch(getVisualBuilderData({ ...newParams, prevTabStatus }, values));
    }, 300);
  };

  const isCollectTab = tabStatus === TAB_STATUSES.COLLECT;
  const isCustomizeTab = tabStatus === TAB_STATUSES.CUSTOMIZE;
  const isHomebaseTab = tabStatus === TAB_STATUSES.HOMEBASE;
  const isPublicTab = tabStatus === TAB_STATUSES.PUBLIC;
  const isPublicMediaShareTab = tabStatus === TAB_STATUSES.PUBLIC_MEDIA_SHARE;
  const isPublicSingleShareTab = tabStatus === TAB_STATUSES.PUBLIC_SINGLE_SHARE;

  useEffect(() => {
    if (!property.id || ((isCustomizeTab || isPublicTab) && !experience.id)) {
      return;
    }

    if ((isHomebaseTab || isCollectTab) && !visualBuilderData.id) {
      const values = {
        ...getPropertyMenuItems(property),
        otherTag: property.otherTag,
        tabChanged: false,
        locked: experience.locked || false,
      };
      fetchVisualBuilderData(values);
    }

    if ((isCustomizeTab || isPublicTab) && !visualBuilderData.id) {
      const values = {
        ...getPropertyMenuItems(property),
        ...getExperienceValues(experience as unknown as Record<string, unknown>),
        otherTag: property.otherTag,
        tabChanged: false,
      };
      fetchVisualBuilderData(values);
    }

    if ((isPublicMediaShareTab || isPublicSingleShareTab) && !visualBuilderData.id) {
      const values = {
        ...getPropertyMenuItems(property),
        otherTag: property.otherTag,
        tabChanged: false,
      };
      fetchVisualBuilderData(values);
    }
  }, [property.id, experience.id, visualBuilderData.id, tabStatus]);

  useEffect(() => {
    if (isPublicTab) {
      return;
    }

    if (prevTabStatus && visualBuilderData.id) {
      fetchVisualBuilderData({ tabChanged: true });
    }
  }, [visualBuilderData.id, tabStatus]);

  useEffect(() => {
    const listener = ({ detail }: { detail: { options?: { propertyId?: number } } }) => {
      const { options = {} } = detail;

      if (
        visualBuilderData.id &&
        options.propertyId != null &&
        propertyId != null &&
        options.propertyId === +propertyId
      ) {
        fetchVisualBuilderData({ tabChanged: false });
      }
    };

    const entities = [ENTITY.MEDIA_ITEM, ENTITY.TAG, ENTITY.EXPERIENCE_MEDIA_ITEM, ENTITY.MEMBER];

    entities.forEach((entity) => document.addEventListener(entity, listener as unknown as EventListener));
    return () =>
      entities.forEach((entity) => document.removeEventListener(entity, listener as unknown as EventListener));
  }, [propertyId, visualBuilderData, experience]);

  const { hiddenTags: experienceHiddenTags = [], hiddenContentTypes: experienceHiddenContentTypes = [] } = experience;

  useEffect(() => {
    if (isPublicTab && visualBuilderData.id) {
      fetchVisualBuilderData({
        ...getExperienceValues(experience as unknown as Record<string, unknown>),
        tabChanged: false,
      });
    }
  }, [JSON.stringify(experienceHiddenTags), JSON.stringify(experienceHiddenContentTypes)]);

  useCurrentExperiencesData({ propertyId, userId, hookEnabled: !!userId });

  useSearchOptionsData({
    params,
    section: SEARCH_OPTION_SECTION.VISUAL_BUILDER,
    entities: [ENTITY.MEDIA_ITEM, ENTITY.MEDIA_ITEM_TAG, ENTITY.MEDIA_ITEM_MEMBER, ENTITY.MEMBER, ENTITY.USER],
  });

  useExperienceMediaItemsData({
    propertyId,
    experienceId,
    hookEnabled: !!experienceId && !!userId && tabStatus !== TAB_STATUSES.PUBLIC,
    tabStatus,
  });

  useElementsData({
    propertyId,
    experienceId,
    sortField: SORT_FIELD.ELEMENT_CREATED_AT,
    sortOrientation: SORT_ORIENTATION.ASC,
    hookEnabled: !!experienceId,
  });

  useContentElementsData({
    propertyId,
    mediaItemId: currentMediaItemId,
    sortField: SORT_FIELD.ELEMENT_CREATED_AT,
    sortOrientation: SORT_ORIENTATION.ASC,
    hookEnabled: !!currentMediaItemId,
  });

  useCurrentExperienceData({ hookEnabled: !!experienceId });

  useCurrentMediaShare();

  useHotspotsData({
    propertyId,
    experienceId,
    elementId,
    sortField: SORT_FIELD.HOTSPOT_ORDER,
    sortOrientation: SORT_ORIENTATION.ASC,
    hookEnabled:
      !!propertyId &&
      !!experienceId &&
      !!elementId &&
      element.type === ELEMENT_TYPE.MAP &&
      element.status !== ELEMENT_STATUS.DRAFT,
  });

  useCurrentMediaItemData({
    propertyId,
    mediaItemId: currentMediaItemId,
  });
  useCurrentShareData();

  useIconsData({
    propertyId,
    sortField: SORT_FIELD.ICON_NAME,
    sortOrientation: SORT_ORIENTATION.ASC,
    hookEnabled: !!userId,
  });
};
